import { markActiveChatAsSeenIfNeeded, cancelPendingMediaRequests } from './redux/TeamInboxThunks';
import { TeamInboxActions } from './redux/TeamInboxReducer';
import { TeamInboxMessagesCacheReducerActions } from '@common-reducers/DBServiceThunks';
import { waQueryMessagesTool } from '@common/tools/whatsapp';
import { apiCall } from '@extension/wa-communication-layer/api/api-call-helper';
import { canonicalMsgKey } from '@common/services/message-vault';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectCurrentChatId, selectCurrentChatMessages } from './redux/TeamInboxSelectors';
import { selectIsDarkMode, selectUserId } from '@common-reducers/UserSelectors';
import { CircularProgress, IconButton } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Stack } from '@mui/system';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import classes from "./team-inbox.module.scss";
import { waWallpaperSx } from '@common/components/wa-wallpaper/wa-wallpaper';
import EmptyState from './empty-state';
import { getDateKey, timestampToDateLabel, computeClusterFlags, ClusterFlags, mimeForMessageType } from './team-inbox-helpers';
import WaMessagesDisplayRow from './wa-messages-display-row';
import { MessageScrollProvider, useMessageScrollController } from './message-scroll-context';
import { decideEnginePage, BACKFILL_RETRY_DELAY_MS } from './engine-page-decision';

// Constants
const FIRST_ITEM_INDEX = 10000; // Large number to allow scrolling up for older messages

type DisplayItem =
    | { type: "header"; timestamp: number }
    | { type: "message"; data: any };

const WaMessageLazyLoadingVirtuoso = React.memo(() => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const userId = useAppSelector(selectUserId);
    const currentChatId = useAppSelector(selectCurrentChatId);
    const isDarkMode = useAppSelector(selectIsDarkMode);

    // === REFS ===
    // Track the previous chat ID to detect changes and cancel old requests
    const previousChatIdRef = useRef<string | null>(null);
    // Monotonic load generation. Every fresh load (chat switch or source-toggle
    // reload) claims the next generation; any in-flight load whose generation is
    // no longer current drops its writes. This makes the LATEST load always win,
    // so rapid chat-switching or fast toggling can't interleave two tiers'
    // messages or leave a chat showing another chat's rows.
    const loadGenRef = useRef(0);
    // Imperative handle to the Virtuoso list (used to pin to bottom on chat open)
    const virtuosoRef = useRef<VirtuosoHandle | null>(null);
    // Chat ID for which the initial scroll-to-bottom was already performed
    const initialScrollChatIdRef = useRef<string | null>(null);
    // Whether the viewport is currently at (or within threshold of) the bottom.
    // Drives the late-settle re-pin below. Starts true: a freshly opened chat
    // is meant to sit at the newest message.
    const atBottomRef = useRef(true);
    // The Virtuoso scroll element. We pin to the bottom by setting its native
    // scrollTop = scrollHeight: Virtuoso's own bottom alignment (followOutput /
    // scrollToIndex 'end') under-scrolls here — it leaves the newest bubble
    // clipped ~46px below the fold because it mis-measures the last item's
    // height. A native scroll to scrollHeight reaches the true bottom and, once
    // content has settled, Virtuoso holds it there (it only re-asserts its short
    // alignment during the initial mount, not afterward).
    const scrollerElRef = useRef<HTMLElement | null>(null);
    // The Box wrapping the Virtuoso, used to resolve the scroller element by
    // query as a fallback (the scrollerRef callback can be missed across some
    // mount/remount timings).
    const containerRef = useRef<HTMLDivElement | null>(null);
    // Pending debounced pin timer (see requestBottomPin).
    const pinTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // Resolve the live scroller element. Prefer the ref captured via Virtuoso's
    // scrollerRef; fall back to a scoped query so the pin still works even if the
    // callback hasn't populated the ref yet.
    const getScroller = useCallback((): HTMLElement | null => {
        return scrollerElRef.current
            || (containerRef.current?.querySelector('[data-testid="virtuoso-scroller"]') as HTMLElement | null)
            || null;
    }, []);

    // Pin to the true bottom, debounced. Each call resets a short timer, so a
    // burst of height changes (Lazy Lexical text, media settling into reserved
    // boxes, the optimistic→live reconcile) collapses into ONE scroll that runs
    // after the content has stopped moving. Doing it once-after-settle — rather
    // than every frame — is what avoids the visible tug-of-war: a single native
    // scroll past Virtuoso's short alignment lands on the true bottom and holds,
    // whereas re-asserting every frame fights Virtuoso's correction and jitters.
    // Skipped when the user has scrolled up to read history (atBottomRef false).
    const requestBottomPin = useCallback(() => {
        if (pinTimerRef.current) clearTimeout(pinTimerRef.current);
        pinTimerRef.current = setTimeout(() => {
            if (!atBottomRef.current) return;
            const el = getScroller();
            if (el) el.scrollTop = el.scrollHeight;
        }, 90);
    }, [getScroller]);

    // Initial-open pin. On a fresh mount Virtuoso applies its own bottom
    // alignment (initialTopMostItemIndex 'end') over several frames, and it
    // under-scrolls — leaving the newest bubble clipped ~46px below the fold. A
    // single native scroll fixes it, but only AFTER Virtuoso stops re-asserting
    // its alignment (else it gets overridden). So: poll until the scroller's
    // scrollTop has held steady for a few frames (Virtuoso done aligning), then
    // do ONE scroll to the true bottom — which then sticks. A short re-verify
    // catches any late re-align. Waiting-then-pinning once avoids the visible
    // flicker that per-frame re-asserting would cause.
    const pinInitialAfterSettle = useCallback(() => {
        let lastTop = -1;
        let stableFrames = 0;
        let attempts = 0;
        const run = () => {
            const el = getScroller();
            if (el) {
                if (el.scrollTop === lastTop) stableFrames++;
                else { stableFrames = 0; lastTop = el.scrollTop; }
                const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
                if ((stableFrames >= 3 && dist > 2) || attempts >= 28) {
                    el.scrollTop = el.scrollHeight;
                    // Re-verify once after a beat in case Virtuoso re-aligns late.
                    setTimeout(() => {
                        const e2 = getScroller();
                        if (e2 && atBottomRef.current && (e2.scrollHeight - e2.scrollTop - e2.clientHeight) > 2) {
                            e2.scrollTop = e2.scrollHeight;
                        }
                    }, 160);
                    return;
                }
            }
            if (++attempts < 30) setTimeout(run, 60);
        };
        run();
    }, [getScroller]);

    useEffect(() => () => { if (pinTimerRef.current) clearTimeout(pinTimerRef.current); }, []);

    // === STATE ===
    // Get messages from the new cache system
    const allMessages = useAppSelector(selectCurrentChatMessages);
    // Get hasMoreMsgsToLoad from TeamInboxReducer
    const hasMoreMsgsToLoad = useAppSelector((state) => state.TeamInboxReducer?.hasMoreMsgsToLoad);
    // Get messages loading state from cache using selectors
    const messagesLoading = useAppSelector(state => state.DBTeamInboxMessagesCacheReducer.itemsLoading);
    const messagesLoadedLocally = useAppSelector(state => state.DBTeamInboxMessagesCacheReducer.itemsLoadedLocaly);

    // === LOCAL STATE ===
    // State for paginated messages
    const [offset, setOffset] = useState(0);

    // === 3-TIER "LOAD OLDER" REFS ===
    // The engine call (waQueryMessagesTool) already returns WhatsApp-store +
    // local-vault messages merged. When it reports the store exhausted we flip
    // to the THIRD tier — the server DB backup — and keep paging older until it
    // too is drained. Refs (not state) so the paging callbacks stay stable.
    const engineExhaustedRef = useRef(false);
    const dbDoneRef = useRef(false);
    const dbCursorRef = useRef<{ t: number; id: string } | null>(null);
    // Freshest ascending message list, mirrored for the DB cursor derivation.
    const allMessagesRef = useRef<any[]>([]);
    // React mirror of atBottomRef, purely to drive the "scroll to bottom" FAB
    // visibility (the ref alone can't re-render). True = at/near bottom → FAB hidden.
    const [isAtBottom, setIsAtBottom] = useState(true);

    // === DEBUG SOURCE GATES (bt-debug only) ===
    // Three toggles — WhatsApp / Vault / DB backup — to verify each retrieval
    // tier independently. Default all ON = the normal WA→vault→DB cascade. A
    // tier that's OFF is skipped (priorities are unchanged; the toggle only says
    // WHETHER to draw from that source). Ref mirror keeps the paging callbacks
    // stable; flipping a toggle resets paging + reloads the chat.
    const showDebugControls = typeof localStorage !== 'undefined' && localStorage.getItem('bt-debug') === 'true';
    const [sourceGates, setSourceGates] = useState({ wa: true, vault: true, backup: true });
    const sourceGatesRef = useRef(sourceGates);
    sourceGatesRef.current = sourceGates;

    // === DERIVED STATE ===
    const isLoading = !messagesLoadedLocally;
    const isEmpty = allMessages.length === 0 && messagesLoadedLocally;

    // Keep the ref in sync so the DB-tier cursor reads the freshest oldest row.
    allMessagesRef.current = allMessages;

    // === FUNCTIONS ===
    // Reset the 3-tier paging state and flush the message mirror. Called on a
    // chat switch and whenever a source toggle flips. The `allMessagesRef = []`
    // is load-bearing: `fetchDbOlder` dedups DB rows against this ref, so a stale
    // (pre-clearState) value makes it drop every DB row as an apparent duplicate
    // — the reason toggling a source off used to blank the list (bug #3).
    const resetTierPaging = useCallback(() => {
        dispatch(TeamInboxMessagesCacheReducerActions.clearState());
        setOffset(0);
        engineExhaustedRef.current = false;
        dbDoneRef.current = false;
        dbCursorRef.current = null;
        allMessagesRef.current = [];
    }, [dispatch]);

    // TIERS 1+2 — the engine tool returns the live WhatsApp store + local vault
    // MERGED (both live in the WA tab's content script), gated by the WA/vault
    // toggles. Returns { count, engineFailed } so the caller can cascade into the
    // DB tier when the engine yields nothing (empty, or WA tab closed/unreachable).
    const fetchMessages = useCallback(async (
        chatId: string,
        msgOffset: number,
        gen?: number,
        attempt = 0,
    ): Promise<{ count: number; engineFailed: boolean; backfillPending: boolean }> => {
        const superseded = () => gen !== undefined && gen !== loadGenRef.current;
        dispatch(TeamInboxMessagesCacheReducerActions.setItemsLoading(true));
        let count = 0;
        let engineFailed = false;
        let backfillPending = false;
        try {
            const gates = sourceGatesRef.current;
            const result = await waQueryMessagesTool({
                chatId,
                limit: 50,
                offset: msgOffset,
                // Debug source gates — WhatsApp store + vault. Absent ⇒ true.
                isSourceWa: gates.wa,
                isSourceVault: gates.vault,
            });
            // A newer load started while we awaited → drop these results.
            if (superseded()) return { count: 0, engineFailed: false, backfillPending: false };

            if (result.success === false) {
                console.error('[team-inbox] waQueryMessagesTool returned failure', { error: result.error });
                engineFailed = true;
                engineExhaustedRef.current = true;
            } else {
                const data = result.data as { messages?: any[]; paging?: { hasMoreInStore?: boolean }; backfilling?: boolean } | undefined;
                const messages = data?.messages || [];
                count = messages.length;
                // `hasMoreInStore` reflects the LIVE store's offset paging. With
                // WhatsApp gated OFF the engine returned vault-only (not offset-
                // paged), so the engine is exhausted after this one pull. While the
                // engine is backfilling from the phone it is NOT exhausted — the
                // caller re-requests this same page shortly (bounded retries).
                const decision = decideEnginePage({
                    waGate: gates.wa,
                    messagesCount: count,
                    hasMoreInStore: data?.paging?.hasMoreInStore,
                    backfilling: data?.backfilling,
                    attempt,
                    limit: 50,
                });
                backfillPending = decision.backfillPending;
                const engineHasMore = decision.engineHasMore;
                if (!engineHasMore) engineExhaustedRef.current = true;
                if (messages.length > 0) {
                    const normalizedMessages = messages.map((msg: any, index: number) => {
                        // Canonical id is waMessageKey._serialized (slim wire contract,
                        // @c.us form since the lid conversion). The remaining fields are
                        // legacy fallbacks for cached entries written by older builds.
                        const actualWaMessageId = msg.waMessageKey?._serialized || msg.waMessageKey?.id ||
                            msg.id?._serialized || msg.key?._serialized || msg.key?.id;
                        let cacheId = actualWaMessageId || msg._id || msg._serialized;
                        if (!cacheId) {
                            const timestamp = msg.timestamp || msg.t || 0;
                            const fromMe = msg.fromMe ? '1' : '0';
                            cacheId = `${chatId}_${timestamp}_${fromMe}_${index}`;
                        }
                        // The slim wire contract emits `t` (unix seconds) as the only
                        // timestamp field; everything downstream (sorting, date headers,
                        // time labels) reads `timestamp` — normalize once at ingestion.
                        return { ...msg, _id: cacheId, chatId, waMessageId: actualWaMessageId, timestamp: msg.timestamp ?? msg.t };
                    });
                    dispatch(TeamInboxMessagesCacheReducerActions.upsertMany(normalizedMessages));
                    // Keep "load older" enabled while EITHER the engine (WhatsApp
                    // store + vault) OR the DB backup tier still has older messages.
                    const backupMayHaveMore = gates.backup && !dbDoneRef.current;
                    dispatch(TeamInboxActions.setHasMoreMsgsToLoad(engineHasMore || backupMayHaveMore));
                } else {
                    // Engine store+vault empty → fall through to the DB tier
                    // (unless the engine is still backfilling this page).
                    dispatch(TeamInboxActions.setHasMoreMsgsToLoad(engineHasMore || (gates.backup && !dbDoneRef.current)));
                }
            }
        } catch (error) {
            if (superseded()) return { count: 0, engineFailed: false, backfillPending: false };
            console.error('[team-inbox] waQueryMessagesTool failed', { error });
            engineFailed = true;
            engineExhaustedRef.current = true;
        }
        // Engine failed (returned failure / threw — e.g. WA tab closed): the only
        // remaining source is the DB backup, so "load older" tracks that gate.
        // (The caller also cascades straight into the DB tier on count===0.)
        if (engineFailed) {
            dispatch(TeamInboxActions.setHasMoreMsgsToLoad(sourceGatesRef.current.backup && !dbDoneRef.current));
        }
        dispatch(TeamInboxMessagesCacheReducerActions.setItemsLoading(false));
        // Empty page while the engine backfills: keep the loading state (not
        // "No messages found") until a retry lands rows or retries run out.
        if (!(backfillPending && count === 0)) {
            dispatch(TeamInboxMessagesCacheReducerActions.setItemsLoadedLocaly(true));
        }
        return { count, engineFailed, backfillPending };
    }, [dispatch]);

    // TIER 3 — server DB backup. Runs once the engine (WhatsApp store + vault)
    // is exhausted, paging older by a strict-before (t,_id) cursor. Rows are
    // deduped against what's already loaded by the addressing-agnostic canonical
    // key (so the @c.us DB copy of a message the engine returned under @lid never
    // shows twice), tagged `source:'backup'`, and folded into the same cache.
    const fetchDbOlder = useCallback(async (chatId: string, gen?: number) => {
        const superseded = () => gen !== undefined && gen !== loadGenRef.current;
        dispatch(TeamInboxMessagesCacheReducerActions.setItemsLoading(true));
        try {
            const loaded = allMessagesRef.current;
            const cursor = dbCursorRef.current;
            let endpoint = `/wa-messages-backup?chatId=${encodeURIComponent(chatId)}&limit=50`;
            if (cursor) {
                endpoint += `&before[t]=${cursor.t}&before[id]=${encodeURIComponent(cursor.id)}`;
            } else {
                // First DB page: everything at/older than the oldest loaded row
                // (ms). The canonical-key dedup below drops the boundary overlap.
                const oldest = loaded[0];
                const oldestSec = oldest?.timestamp ?? oldest?.t ?? 0;
                if (oldestSec > 0) {
                    const oldestMs = oldestSec > 1e12 ? oldestSec : oldestSec * 1000;
                    endpoint += `&tLte=${oldestMs}`;
                }
            }
            const rows = await apiCall<any[]>('get', endpoint);
            // A newer load started while we awaited → drop these results.
            if (superseded()) return;
            const docs = Array.isArray(rows) ? rows : [];

            const loadedKeys = new Set(
                loaded.map((m: any) => canonicalMsgKey(m.waMessageKey?._serialized || m._id || '')),
            );
            const fresh = docs.filter((d) => !loadedKeys.has(canonicalMsgKey(d.msgId || '')));
            if (fresh.length > 0) {
                const normalized = fresh.map((d) => {
                    const tSec = (Number(d.t) || 0) > 1e12 ? Math.floor(Number(d.t) / 1000) : Number(d.t) || 0;
                    // If the backend signed a full-media URL (mediaPointer exists),
                    // pre-load it so the bubble renders full-res; otherwise the
                    // requestMessageMediaThunk thumbnail fallback (from `body`) kicks in.
                    const mediaLoaded = typeof d.mediaUrl === 'string' && d.mediaUrl.length > 0
                        ? {
                            mediaStatus: 'loaded',
                            // mimetype is REQUIRED for the preview to pick a renderer
                            // (asset.type); without it a backed-up image/doc bubble
                            // renders blank even with the full-res URL set.
                            mediaData: {
                                url: d.mediaUrl,
                                mediaUrl: d.mediaUrl,
                                mimetype: mimeForMessageType(d.type, d.filename ?? d.caption),
                                filename: d.filename ?? undefined,
                            },
                        }
                        : {};
                    return {
                        waMessageKey: { _serialized: d.msgId },
                        _id: d.msgId,
                        waMessageId: d.msgId,
                        chatId: d.chatId,
                        from: d.senderId ?? '',
                        author: d.senderId ?? null,
                        notifyName: d.authorName ?? null,
                        type: d.type || 'chat',
                        body: d.body ?? '',
                        caption: d.caption ?? null,
                        filename: null,
                        fromMe: !!d.fromMe,
                        hasAttachedMedia: !!d.hadMedia,
                        t: tSec,
                        timestamp: tSec,
                        source: 'backup',
                        // Engine-stamped reason the media wasn't backed up (shown to
                        // the user next to the thumbnail). Absent when it backed up.
                        mediaSkipReason: d.mediaSkipReason ?? undefined,
                        ...mediaLoaded,
                    };
                });
                dispatch(TeamInboxMessagesCacheReducerActions.upsertMany(normalized as any));
            }
            // Advance the cursor from the last DB row (even if all were dupes, to
            // keep making progress), and stop when the DB returns a short page.
            const last = docs[docs.length - 1];
            if (last) dbCursorRef.current = { t: Number(last.t) || 0, id: String(last._id) };
            if (docs.length < 50) {
                dbDoneRef.current = true;
                dispatch(TeamInboxActions.setHasMoreMsgsToLoad(false));
            }
        } catch (error) {
            if (superseded()) return;
            console.error('[team-inbox] DB backup tier fetch failed', { error });
            dbDoneRef.current = true;
            dispatch(TeamInboxActions.setHasMoreMsgsToLoad(false));
        } finally {
            // Only the current load owns the shared loading flag.
            if (!superseded()) dispatch(TeamInboxMessagesCacheReducerActions.setItemsLoading(false));
        }
    }, [dispatch]);

    // Engine tier yielded nothing for the initial page (empty, or WA tab closed)
    // → continue straight into the DB backup tier. fetchMessages has already
    // marked the engine exhausted in that case.
    const cascadeToDbIfEmpty = useCallback(async (chatId: string, count: number, gen: number) => {
        if (count === 0 && sourceGatesRef.current.backup && !dbDoneRef.current) {
            await fetchDbOlder(chatId, gen);
        }
    }, [fetchDbOlder]);

    // The engine answered `backfilling:true` for a page: re-request the SAME page
    // after a short delay so the phone-fetched rows land without reopening the
    // chat (upserts are keyed by id, so overlap is harmless). Bounded by
    // decideEnginePage (MAX_BACKFILL_RETRIES); dropped if the load is superseded.
    const scheduleBackfillRetry = useCallback((chatId: string, msgOffset: number, gen: number, attempt: number) => {
        setTimeout(async () => {
            if (gen !== loadGenRef.current) return;
            const r = await fetchMessages(chatId, msgOffset, gen, attempt);
            if (gen !== loadGenRef.current) return;
            if (r.backfillPending) {
                scheduleBackfillRetry(chatId, msgOffset, gen, attempt + 1);
            } else if (msgOffset === 0) {
                // Initial page stayed empty after backfill → the deferred DB cascade.
                await cascadeToDbIfEmpty(chatId, r.count, gen);
            }
        }, BACKFILL_RETRY_DELAY_MS);
    }, [fetchMessages, cascadeToDbIfEmpty]);

    // Unmount supersedes any in-flight load / pending backfill retry (the
    // generation guard drops it), same as a chat switch or source toggle.
    useEffect(() => () => { loadGenRef.current++; }, []);

    // Initial load for a chat: run the enabled tiers in cascade order
    // (WhatsApp+vault → DB backup). The engine→DB FALLBACK is the fix for
    // "WA closed shows nothing" (bug #2): if the engine tier returns zero rows
    // — genuinely empty OR because the WA tab is closed/unreachable — and the
    // backup gate is on, continue straight into the DB tier instead of leaving
    // the chat blank waiting for a scroll that can't happen on an empty list.
    const fetchInitialMessageArray = useCallback(async (chatId: string) => {
        // Claim the latest load generation; any older in-flight load now drops
        // its writes when it resolves (rapid chat-switch / toggle safety).
        const gen = ++loadGenRef.current;
        try {
            const gates = sourceGatesRef.current;
            if (gates.wa || gates.vault) {
                const { count, backfillPending } = await fetchMessages(chatId, 0, gen);
                if (loadGenRef.current !== gen) return;
                setOffset(50);
                if (backfillPending) {
                    scheduleBackfillRetry(chatId, 0, gen, 1);
                } else {
                    await cascadeToDbIfEmpty(chatId, count, gen);
                }
            } else if (gates.backup) {
                // Both engine tiers OFF → skip straight to the DB backup tier.
                engineExhaustedRef.current = true;
                dispatch(TeamInboxMessagesCacheReducerActions.setItemsLoadedLocaly(true));
                await fetchDbOlder(chatId, gen);
            } else {
                // Nothing enabled — mark loaded so the empty state renders.
                dispatch(TeamInboxMessagesCacheReducerActions.setItemsLoadedLocaly(true));
                dispatch(TeamInboxActions.setHasMoreMsgsToLoad(false));
            }
        } catch (error) {
            console.error("Failed to load MessageArray:", error);
        }
    }, [fetchMessages, fetchDbOlder, cascadeToDbIfEmpty, scheduleBackfillRetry, dispatch]);

    const loadOlderMessages = useCallback(() => {
        if (!messagesLoadedLocally || messagesLoading || !currentChatId) return;
        const gates = sourceGatesRef.current;
        const engineEnabled = gates.wa || gates.vault;
        if (engineEnabled && !engineExhaustedRef.current) {
            // Tiers 1+2: WhatsApp store + local vault (merged + gated by the engine).
            const gen = loadGenRef.current;
            const chatId = currentChatId;
            const pageOffset = offset;
            fetchMessages(chatId, pageOffset, gen).then(({ backfillPending }) => {
                if (backfillPending && gen === loadGenRef.current) scheduleBackfillRetry(chatId, pageOffset, gen, 1);
            });
            setOffset(prevOffset => prevOffset + 50);
        } else if (gates.backup && !dbDoneRef.current) {
            // Tier 3: server DB backup.
            fetchDbOlder(currentChatId, loadGenRef.current);
        }
    }, [messagesLoadedLocally, messagesLoading, fetchMessages, fetchDbOlder, scheduleBackfillRetry, offset, currentChatId]);

    // === DISPLAY LIST LOGIC ===
    const displayItems = useMemo((): DisplayItem[] => {
        const items: DisplayItem[] = [];
        let lastKey: string | null = null;

        allMessages.forEach(msg => {
            const dayKey = getDateKey(msg.timestamp);

            if (dayKey !== lastKey) {
                items.push({ type: "header", timestamp: msg.timestamp });
                lastKey = dayKey;
            }

            items.push({ type: "message", data: msg });
        });

        return items;
    }, [allMessages]);

    // Calculate the first item index for bottom-to-top display.
    // Must be derived from displayItems (messages + date headers), not raw
    // messages: when older messages are prepended via startReached, Virtuoso
    // anchors the viewport by how much firstItemIndex decreased — that delta
    // has to equal the exact number of prepended display rows (including any
    // new date headers) or the view jumps.
    const firstItemIndex = useMemo(() => {
        return Math.max(0, FIRST_ITEM_INDEX - displayItems.length);
    }, [displayItems.length]);

    // Per-message cluster flags (first/last of a same-sender consecutive run).
    // Computed from the sorted message array's neighbours — date headers only sit
    // at day boundaries, which computeClusterFlags already treats as a break.
    const clusterFlagsById = useMemo(() => {
        const map = new Map<string, ClusterFlags>();
        for (let i = 0; i < allMessages.length; i++) {
            const cur = allMessages[i];
            const id = cur?._id ?? cur?.id?._serialized;
            if (!id) continue;
            map.set(id, computeClusterFlags(allMessages[i - 1] ?? null, cur, allMessages[i + 1] ?? null));
        }
        return map;
    }, [allMessages]);

    // Scroll-to-quoted-message controller (imperative Virtuoso handle + flash).
    // Reply-paging deps let it fetch older history until the quoted original
    // surfaces (reuses loadOlderMessages + the paging flags).
    const scrollController = useMessageScrollController(virtuosoRef, displayItems, {
        loadOlder: loadOlderMessages,
        hasMore: !!hasMoreMsgsToLoad,
        isLoading: !!messagesLoading,
    });

    // === RENDER FUNCTIONS ===
    const renderItem = useCallback((item: DisplayItem) => {
        switch (item.type) {
            case "header":
                // WhatsApp-style centered date chip.
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: '8px' }}>
                        <Box sx={{
                            backgroundColor: 'var(--wa-date-chip-bg, #fff)',
                            color: 'var(--wa-secondary-text, #54656f)',
                            fontSize: '12.5px',
                            fontWeight: 500,
                            lineHeight: '20px',
                            padding: '5px 12px',
                            borderRadius: '7.5px',
                            boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.2px',
                            userSelect: 'none',
                        }}>
                            {timestampToDateLabel(item.timestamp, false)}
                        </Box>
                    </Box>
                );
            case "message":
                // Use _id (which contains the generated ID) or fallback to id._serialized
                const itemId = item.data?._id || item.data?.id?._serialized;
                const flags = clusterFlagsById.get(itemId);
                return (
                    <WaMessagesDisplayRow
                        itemId={itemId}
                        isFirstOfCluster={flags?.isFirstOfCluster ?? true}
                        isLastOfCluster={flags?.isLastOfCluster ?? true}
                    />
                );
            default:
                return null;
        }
    }, [clusterFlagsById]);

    // === EFFECTS ===
    // Handle chat changes - cancel old requests, reset state, and fetch new messages immediately
    useEffect(() => {
        const previousChatId = previousChatIdRef.current;
        const chatChanged = previousChatId !== null && previousChatId !== currentChatId;

        // If chat changed, cancel pending media requests and reset state
        if (chatChanged) {
            dispatch(cancelPendingMediaRequests());
            resetTierPaging();
        }

        previousChatIdRef.current = currentChatId;

        // Fetch messages for the new/current chat immediately. The load-generation
        // guard supersedes any still-in-flight load from the previous chat.
        if (userId && currentChatId) {
            fetchInitialMessageArray(currentChatId);
        }
    }, [currentChatId, userId, dispatch, fetchInitialMessageArray, resetTierPaging]);

    // Pin the view to the newest (bottom) message when a chat is opened.
    // initialTopMostItemIndex only applies at Virtuoso mount time; messages
    // load asynchronously, so on first open (or chat switch) the list can
    // mount/populate after that index was consumed. Re-apply it imperatively
    // exactly once per chat, as soon as the list is rendered with data.
    useEffect(() => {
        if (!currentChatId || isLoading || displayItems.length === 0) return;
        if (initialScrollChatIdRef.current === currentChatId) return;
        initialScrollChatIdRef.current = currentChatId;
        atBottomRef.current = true;
        pinInitialAfterSettle();
    }, [currentChatId, isLoading, displayItems.length, pinInitialAfterSettle]);

    // Keep the view pinned to the bottom when new messages arrive while the
    // user is at (or near) the bottom; don't yank them down if they scrolled up.
    // Use instant ('auto') not 'smooth': a smooth animation runs while the new
    // row is still settling its height (RTL reflow, ack icon, the optimistic→
    // live reconcile), so it lands a few px above the bottom — the "little
    // jump". Instant snaps exactly to the bottom every time.
    const followOutput = useCallback((isAtBottom: boolean) => {
        return isAtBottom ? ('auto' as const) : false;
    }, []);

    // Re-pin to the true bottom whenever the total content height changes while
    // the user is following the conversation. This is the fix for the "lands a
    // bit above the bottom" jump: a bubble's height grows AFTER followOutput
    // already scrolled — the Lexical text editor and the time/ack row render
    // asynchronously, and media settles into its reserved box — so the newest
    // message ends up below the fold. followOutput only reacts to NEW items, not
    // to a rendered item growing; this closes that gap. The pin is debounced, so
    // a burst of height changes resolves into one scroll once everything stops
    // moving (no per-frame fighting with Virtuoso's own alignment).
    const handleTotalListHeightChanged = useCallback(() => {
        requestBottomPin();
    }, [requestBottomPin]);

    const handleAtBottomStateChange = useCallback((atBottom: boolean) => {
        atBottomRef.current = atBottom;
        setIsAtBottom(atBottom);
    }, []);

    // "Scroll to bottom" FAB action: jump to the newest message (data-relative
    // last index) with a smooth animation.
    const scrollToBottom = useCallback(() => {
        const lastIndex = displayItems.length - 1;
        if (lastIndex < 0) return;
        atBottomRef.current = true;
        virtuosoRef.current?.scrollToIndex({ index: lastIndex, align: 'end', behavior: 'smooth' });
    }, [displayItems.length]);

    // Debug "scroll to top" — jump to the oldest loaded row; reaching the top
    // also fires startReached → loadOlder, so it doubles as "load older".
    const scrollToTop = useCallback(() => {
        virtuosoRef.current?.scrollToIndex({ index: 0, align: 'start', behavior: 'smooth' });
    }, []);

    // Flipping a source toggle → reset the 3-tier paging state and reload the
    // current chat from scratch with the new gates (skip the initial mount).
    // resetTierPaging() flushes the message mirror synchronously, so the DB tier
    // reloads immediately instead of dropping every row as a stale duplicate
    // (bug #3 — previously you had to toggle a source off-then-on to see it).
    const gatesInitRef = useRef(true);
    useEffect(() => {
        if (gatesInitRef.current) { gatesInitRef.current = false; return; }
        if (!currentChatId) return;
        resetTierPaging();
        fetchInitialMessageArray(currentChatId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceGates.wa, sourceGates.vault, sourceGates.backup]);

    // Mark chat as seen after messages are loaded
    useEffect(() => {
        if (messagesLoadedLocally && allMessages.length <= 50) {
            dispatch(markActiveChatAsSeenIfNeeded());
        }
    }, [messagesLoadedLocally, allMessages.length, dispatch]);

    // === RENDER ===
    return (
        <Stack
            className={isDarkMode ? classes.darkMessageBackground : classes.lightMessageBackground}
            sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                alignItems: 'center',
                // WA doodle tile lives in public/img — not bundleable from a
                // css-module url(). JS supplies both tile URLs; CSS picks one
                // from the `.dark` ancestor (see wa-wallpaper.ts).
                ...waWallpaperSx(),
            }}>
            {/* Loading state */}
            {isLoading && (
                <Box className={classes.loadingContainer}>
                    <CircularProgress />
                </Box>
            )}

            {/* Empty state */}
            {isEmpty && (
                <EmptyState description={t('teamInbox.noMessagesFound')} />
            )}

            {/* Messages list */}
            {!isLoading && !isEmpty && (
                <MessageScrollProvider value={scrollController}>
                <Box className={classes.virtuosoContainer} ref={containerRef}>
                    <Virtuoso
                        // Remount per chat (and per source-gate change) so
                        // initialTopMostItemIndex (mount-only) re-applies, no scroll
                        // state leaks between chats, and a debug tier switch re-pins
                        // to the newest row cleanly instead of fighting the
                        // firstItemIndex anchor on an in-place data swap. In
                        // production the gates never change, so the key is stable.
                        key={`${currentChatId ?? 'no-chat'}|${sourceGates.wa ? 1 : 0}${sourceGates.vault ? 1 : 0}${sourceGates.backup ? 1 : 0}`}
                        ref={virtuosoRef}
                        scrollerRef={(el) => { scrollerElRef.current = (el as HTMLElement) ?? null; }}
                        className={classes.virtuosoScroll}
                        data={displayItems}
                        firstItemIndex={firstItemIndex}
                        startReached={loadOlderMessages}
                        // Data-relative index of the last item: start pinned to the
                        // newest message (must NOT be the absolute FIRST_ITEM_INDEX
                        // offset — Virtuoso expects an index into `data` here)
                        initialTopMostItemIndex={{ index: displayItems.length - 1, align: 'end' }}
                        followOutput={followOutput}
                        // Pin content to the bottom edge (WhatsApp-style) when the
                        // list doesn't fill the viewport, and treat "within 280px
                        // of the bottom" as at-bottom: appending a message (incl. a
                        // reserved 200px media box) briefly grows the distance, and
                        // this keeps the auto-follow + debounced pin engaged through
                        // that transient instead of disengaging mid-send.
                        alignToBottom
                        // Tight threshold (WA feel): a user scrolled up even a
                        // little is NOT force-followed on a new inbound message.
                        // followOutput + requestBottomPin both gate on this
                        // at-bottom flag, so the initial-open native pin (which
                        // sets atBottomRef true + scrolls to scrollHeight) still
                        // lands exactly on the newest message.
                        atBottomThreshold={48}
                        atBottomStateChange={handleAtBottomStateChange}
                        totalListHeightChanged={handleTotalListHeightChanged}
                        totalCount={displayItems.length}
                        components={{
                            // Fixed-height header so toggling the paging spinner
                            // never nudges the viewport: while more history exists
                            // the header is a CONSTANT 44px box with the spinner as
                            // a centered overlay (present only while loading). Once
                            // history is exhausted it collapses to a static 16px pad
                            // (a one-time change, not during active paging).
                            Header: () => (
                                hasMoreMsgsToLoad ? (
                                    <Box sx={{ position: 'relative', height: '44px' }}>
                                        {messagesLoading && (
                                            <Stack
                                                direction="row"
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                }}
                                            >
                                                <CircularProgress size={16} thickness={2} />
                                                <span style={{ fontSize: '13px', color: 'var(--wa-secondary-text, #54656f)' }}>
                                                    {t('teamInbox.loadingMoreMessages')}
                                                </span>
                                            </Stack>
                                        )}
                                    </Box>
                                ) : (<div style={{ height: '16px' }}></div>)
                            ),
                            Footer: () => <div style={{ height: '16px' }}></div>,
                        }}
                        itemContent={(_, item) => (
                            <Box className={classes.virtuosoItemContainer}>
                                {renderItem(item)}
                            </Box>
                        )}
                    />
                </Box>
                </MessageScrollProvider>
            )}

            {/* Debug source controls (bt-debug only): 3 tier toggles + a
                "scroll to top" button, stacked above the scroll-to-bottom FAB.
                Toggling a source resets paging and reloads the chat, so you can
                verify each tier (WhatsApp / vault / DB backup) in isolation.
                Gated on chat-open (NOT !isLoading) so the toggles stay put while a
                switch reloads — otherwise they vanish mid-reload and a quick second
                toggle lands on nothing. */}
            {showDebugControls && !!currentChatId && (
                <Box sx={{
                    position: 'absolute', bottom: '70px', right: '18px', zIndex: 6,
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px',
                }}>
                    <Box sx={{
                        display: 'flex', flexDirection: 'column', gap: '6px',
                        backgroundColor: 'var(--wa-panel-bg, #fff)', padding: '8px 10px',
                        borderRadius: '10px', boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                    }}>
                        {([
                            { key: 'wa', label: 'WhatsApp', color: '#25D366' },
                            { key: 'vault', label: 'Vault', color: '#f59e0b' },
                            { key: 'backup', label: 'DB', color: '#9e9e9e' },
                        ] as const).map(({ key, label, color }) => {
                            const on = sourceGates[key];
                            return (
                                <Box
                                    key={key}
                                    onClick={() => setSourceGates((g) => ({ ...g, [key]: !g[key] }))}
                                    sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', userSelect: 'none' }}
                                >
                                    <Box sx={{ position: 'relative', width: 30, height: 16, borderRadius: '8px', backgroundColor: on ? color : '#d0d0d0', transition: 'background-color .15s' }}>
                                        <Box sx={{ position: 'absolute', top: 2, left: on ? 16 : 2, width: 12, height: 12, borderRadius: '50%', backgroundColor: '#fff', transition: 'left .15s' }} />
                                    </Box>
                                    <span style={{ fontSize: 11.5, fontWeight: 600, minWidth: 58, color: on ? 'var(--primary-text-color, #111b21)' : '#9aa0a6' }}>{label}</span>
                                </Box>
                            );
                        })}
                    </Box>
                    <IconButton
                        onClick={scrollToTop}
                        aria-label={t('teamInbox.scrollToTop', { defaultValue: 'Scroll to top' })}
                        sx={{
                            width: 42, height: 42,
                            backgroundColor: 'var(--wa-panel-bg, #fff)',
                            color: 'var(--wa-secondary-text, #54656f)',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                            '&:hover': { backgroundColor: 'var(--wa-hover-action-bg, #f5f6f6)' },
                        }}
                    >
                        <KeyboardArrowUpIcon />
                    </IconButton>
                </Box>
            )}

            {/* WhatsApp-style "scroll to bottom" FAB — shown only when the user
                has scrolled up away from the newest message. */}
            {!isLoading && !isEmpty && !isAtBottom && (
                <IconButton
                    onClick={scrollToBottom}
                    aria-label={t('teamInbox.scrollToBottom', { defaultValue: 'Scroll to bottom' })}
                    sx={{
                        position: 'absolute',
                        bottom: '18px',
                        right: '18px',
                        width: 42,
                        height: 42,
                        zIndex: 5,
                        backgroundColor: 'var(--wa-panel-bg, #fff)',
                        color: 'var(--wa-secondary-text, #54656f)',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                        '&:hover': { backgroundColor: 'var(--wa-hover-action-bg, #f5f6f6)' },
                    }}
                >
                    <KeyboardArrowDownIcon />
                </IconButton>
            )}
        </Stack>
    );
});

export default WaMessageLazyLoadingVirtuoso;
